import { useState, useEffect } from 'react';

interface Phone {
  label: string;
  number: string;
}

interface SocialNetwork {
  network: string;
  enabled: boolean;
  url: string;
}

interface Service {
  id: number;
  title: string;
  short_description: string;
  icon: string;
  order: number;
}

interface PortfolioItem {
  id: number;
  title: string;
  description: string;
  image: string;
  category: string;
  link: string;
  order: number;
}

interface Testimonial {
  id: number;
  author_name: string;
  text: string;
  source: string;
  approved: boolean;
}

export interface SiteContent {
  hero: {
    title: string;
    subtitle: string;
    cta_text: string;
    cta_link: string;
    background_image: string;
  };
  services: Service[];
  portfolio: PortfolioItem[];
  contact: {
    address: string;
    city: string;
    state: string;
    zip: string;
    phones: Phone[];
    email: string;
    business_hours: string;
    service_area: string;
    map_query: string;
  };
  testimonials: Testimonial[];
  footer: {
    footer_address: string;
    footer_phone: string;
    footer_email: string;
    footer_business_hours: string;
    social_networks: SocialNetwork[];
  };
  about: {
    about_title: string;
    about_subtitle: string;
    about_content: string;
    about_image: string;
  };
}

export function useSiteContent() {
  const [content, setContent] = useState<SiteContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      setLoading(true);
      const response = await fetch('/backend/api/get_site_content.php');
      
      if (!response.ok) {
        throw new Error('Failed to fetch site content');
      }

      const data = await response.json();
      
      if (data.success) {
        setContent(data.data);
        setError(null);
      } else {
        throw new Error(data.message || 'Failed to load content');
      }
    } catch (err) {
      console.error('Error fetching site content:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  return { content, loading, error, refetch: fetchContent };
}
